// src/pages/PublicComboDetail.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FaArrowLeft, FaCalendarCheck, FaCheckCircle, FaExternalLinkAlt, FaHandshake, FaMapMarkerAlt, FaShieldAlt, FaStore } from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { buildForfeoComComboUrl, comboAction, formatMoney } from '../lib/combos';

export default function PublicComboDetail() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [combo, setCombo] = useState<any>(null);
  const [selectedSlotId, setSelectedSlotId] = useState('');
  const [showExit, setShowExit] = useState(false);

  useEffect(() => {
    let mounted = true;
    comboAction('public_detail', { slug })
      .then((res: any) => {
        if (!mounted) return;
        setCombo(res.combo || null);
        const first = (res.combo?.time_slots || []).find((s: any) => Number(s.remaining_capacity ?? s.capacity ?? 0) > 0);
        if (first) setSelectedSlotId(first.id);
      })
      .catch((err) => console.error('Combo detail:', err))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [slug]);

  const selectedSlot = useMemo(() => (combo?.time_slots || []).find((s: any) => s.id === selectedSlotId), [combo, selectedSlotId]);
  const forfeoComUrl = useMemo(() => buildForfeoComComboUrl(combo, selectedSlot), [combo, selectedSlot]);

  const reserve = () => {
    if (!selectedSlot) return alert('Choisis une plage horaire avant de continuer.');
    setShowExit(true);
  };

  if (loading) return <div className="min-h-screen pt-32 text-center font-black">Chargement...</div>;
  if (!combo) return <div className="min-h-screen pt-32 text-center"><h1 className="text-3xl font-black">Combo introuvable</h1><Link to="/combos" className="text-blue-600 font-bold">Retour aux Combos</Link></div>;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <Header />
      {showExit && (
        <div className="fixed inset-0 z-[999] bg-slate-950/80 backdrop-blur flex items-center justify-center p-4">
          <div className="max-w-lg w-full bg-white rounded-[2rem] p-8 shadow-2xl">
            <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-700 flex items-center justify-center mb-5"><FaExternalLinkAlt /></div>
            <h2 className="text-2xl font-black">Tu quittes Forfeo Explore</h2>
            <p className="mt-3 text-slate-600 leading-relaxed">Le paiement officiel du Combo se fait sur Forfeo.com. La plage horaire choisie sera transmise avec ta réservation.</p>
            <div className="mt-5 rounded-2xl bg-slate-50 p-4 text-sm">
              <p className="font-black">{combo.title}</p>
              <p className="text-slate-600">{selectedSlot?.date} · {selectedSlot?.start_time} à {selectedSlot?.end_time}</p>
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button onClick={() => setShowExit(false)} className="flex-1 rounded-2xl border border-slate-200 px-5 py-3 font-black">Rester ici</button>
              <a href={forfeoComUrl} className="flex-1 rounded-2xl bg-slate-900 text-white px-5 py-3 font-black text-center">Continuer vers Forfeo.com</a>
            </div>
          </div>
        </div>
      )}

      <main className="pt-28">
        <section className="max-w-7xl mx-auto px-4 pb-10">
          <Link to="/combos" className="inline-flex items-center gap-2 text-sm font-black text-blue-700 mb-6"><FaArrowLeft /> Tous les Combos</Link>
          <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_.85fr] gap-8">
            <div>
              <div className="rounded-[2.5rem] overflow-hidden bg-slate-900 h-[360px] relative">
                {combo.image_url ? <img src={combo.image_url} alt={combo.title} className="w-full h-full object-cover" /> : null}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 text-white">
                  <div className="inline-flex items-center gap-2 rounded-full bg-white/15 border border-white/20 px-3 py-1 text-xs font-black mb-3"><FaHandshake /> Combo Forfeo</div>
                  <h1 className="text-4xl md:text-6xl font-black leading-none">{combo.title}</h1>
                </div>
              </div>
              <div className="mt-8 bg-white rounded-[2rem] border border-slate-200 p-8">
                <h2 className="text-2xl font-black mb-3">L’expérience</h2>
                <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">{combo.description}</p>
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                  {(combo.participants || []).map((p: any) => (
                    <div key={p.id} className="rounded-2xl border border-slate-200 p-4">
                      <FaStore className="text-blue-600 mb-3" />
                      <p className="font-black">{p.business_name || p.name || 'Partenaire Forfeo'}</p>
                      <p className="text-xs text-slate-500 mt-1">{p.role_label || p.category || 'Portion du Combo'}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-6 rounded-[2rem] bg-amber-50 border border-amber-100 p-6 text-amber-950 flex gap-4">
                <FaShieldAlt className="text-amber-600 shrink-0 mt-1" />
                <p className="text-sm leading-relaxed">Chaque entreprise participante est responsable de sa portion. Forfeo agit comme plateforme technologique de coordination, visibilité et paiement.</p>
              </div>
            </div>

            <aside className="lg:sticky lg:top-24 self-start bg-white rounded-[2.5rem] border border-slate-200 shadow-xl p-6">
              <div className="flex items-center gap-2 text-sm font-black text-slate-500 uppercase tracking-widest"><FaMapMarkerAlt /> {combo.city || combo.region || 'Québec'}</div>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <div className="rounded-2xl bg-slate-50 p-4"><p className="text-xs uppercase font-black text-slate-400">Prix Combo</p><p className="text-2xl font-black">{formatMoney(combo.suggested_price)}</p></div>
                <div className="rounded-2xl bg-slate-50 p-4"><p className="text-xs uppercase font-black text-slate-400">Valeur</p><p className="text-2xl font-black">{formatMoney(combo.total_value)}</p></div>
              </div>
              <div className="mt-6">
                <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-3">Choisir une plage horaire</label>
                <div className="space-y-2 max-h-72 overflow-auto pr-1">
                  {(combo.time_slots || []).map((slot: any) => {
                    const remaining = Number(slot.remaining_capacity ?? slot.capacity ?? 0);
                    const disabled = remaining <= 0 || slot.status === 'sold_out';
                    return (
                      <button key={slot.id} disabled={disabled} onClick={() => setSelectedSlotId(slot.id)} className={`w-full text-left rounded-2xl border p-4 transition ${selectedSlotId === slot.id ? 'border-blue-600 bg-blue-50' : 'border-slate-200 bg-white hover:bg-slate-50'} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}>
                        <div className="flex justify-between gap-3"><span className="font-black">{slot.date}</span><span className="text-sm font-bold text-slate-500">{slot.start_time} - {slot.end_time}</span></div>
                        <div className="mt-1 text-xs font-black uppercase text-slate-400">{remaining > 0 ? `${remaining} place(s) restante(s)` : 'Complet'}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <button onClick={reserve} className="mt-6 w-full rounded-2xl bg-gradient-to-r from-blue-600 to-cyan-500 text-white py-4 font-black uppercase tracking-widest shadow-lg hover:brightness-110 transition flex items-center justify-center gap-2"><FaCalendarCheck /> Réserver ce Combo</button>
              <div className="mt-4 flex items-start gap-2 text-xs text-slate-500 leading-relaxed"><FaCheckCircle className="text-emerald-500 mt-0.5 shrink-0" /> Le paiement et la confirmation finale se feront sur Forfeo.com.</div>
            </aside>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
